import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { trigger, state, style, transition, animate } from '@angular/animations';

/**
 * Expansion Column Component
 * Renders the expand/collapse icon button for expandable rows
 */
@Component({
  selector: 'app-expansion-column',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  template: `
    <button
      mat-icon-button
      class="expansion-toggle"
      [class.expanded]="expanded"
      (click)="onToggle($event)"
      [disabled]="disabled"
      [attr.aria-label]="expanded ? 'Collapse row' : 'Expand row'"
      [attr.aria-expanded]="expanded">
      <mat-icon [@rotateIcon]="expanded ? 'expanded' : 'collapsed'">
        {{ expanded ? collapseIcon : expandIcon }}
      </mat-icon>
    </button>
  `,
  styles: [`
    :host {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .expansion-toggle {
      width: 32px;
      height: 32px;
      line-height: 32px;
      transition: background-color 200ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    .expansion-toggle:hover:not([disabled]) {
      background-color: rgba(0, 0, 0, 0.04);
    }

    .dark .expansion-toggle:hover:not([disabled]) {
      background-color: rgba(255, 255, 255, 0.08);
    }

    .expansion-toggle mat-icon {
      transition: transform 225ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    .expansion-toggle[disabled] {
      opacity: 0.3;
      cursor: not-allowed;
    }
  `],
  animations: [
    trigger('rotateIcon', [
      state('collapsed', style({
        transform: 'rotate(0deg)'
      })),
      state('expanded', style({
        transform: 'rotate(180deg)'
      })),
      transition('collapsed <=> expanded', animate('225ms cubic-bezier(0.4, 0, 0.2, 1)'))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpansionColumnComponent {
  /**
   * Whether the row is currently expanded
   */
  @Input() expanded: boolean = false;

  /**
   * Disable the expansion toggle
   */
  @Input() disabled: boolean = false;

  /**
   * Expand icon (Material icon name)
   * @default 'chevron_right'
   */
  @Input() expandIcon: string = 'chevron_right';

  /**
   * Collapse icon (Material icon name)
   * @default 'expand_more'
   */
  @Input() collapseIcon: string = 'expand_more';

  /**
   * Emitted when toggle button is clicked
   */
  @Output() toggle = new EventEmitter<void>();

  /**
   * Handle toggle button click
   */
  onToggle(event: MouseEvent): void {
    event.stopPropagation(); // Prevent row click event
    this.toggle.emit();
  }
}
